import { FESTIVAL, OPPOSITE, axisOf, entrySpot, exitSpot, pathId } from '../../data/full/festival';
import type { EdgeSide } from '../../data/types';
import { clamp, clamp01 } from '../../shared/scalar';
import { isBarTick } from '../../shared/tempo';
import { pathWithObstacles } from '../../data/full/path-layout';
import { lookup } from '../lookup';
import { enterSet } from '../enter-set';
import type { GameMode } from '../mode';
import type { FestivalExit, FestivalState, PlayerState, SimState, Vec2 } from '../state';
import { applyDerivedStats } from '../stats';
import { isSetFinished } from '../systems/set-progress';
import type { StepContext } from '../systems/types';
import { festivalBotMove } from './festival-bot';
import { heldScale } from './festival-scale';

export const festival: GameMode = {
  start({ state }) {
    if (!FESTIVAL.setIds.includes(state.setId)) {
      throw new Error(`the festival does not play the set "${state.setId}"`);
    }
    if (state.set.endlessFromTick !== undefined) {
      throw new Error('a festival does not go on endlessly');
    }
    state.festival = {
      phase: 'scene',
      scenes: [{ setId: state.setId, fromTick: state.tick }],
      exits: [],
      standing: state.players.map(() => null),
      departTicks: 0,
    };
  },
  systems: [festivalStep],
  finishWins: ({ state }) => stagesLeft(festivalOf(state)).length === 0,
  botMove: festivalBotMove,
};

function festivalOf(state: SimState): FestivalState {
  if (state.festival === undefined) {
    throw new Error('a festival game has no festival state');
  }
  return state.festival;
}

function stagesLeft(festival: FestivalState): string[] {
  return FESTIVAL.setIds.filter((id) => !festival.scenes.some((scene) => scene.setId === id));
}

function festivalStep(ctx: StepContext): void {
  const festival = festivalOf(ctx.state);
  switch (festival.phase) {
    case 'scene':
      if (isSetFinished(ctx.state.set, ctx.set)) {
        holdStage(ctx, festival);
      }
      return;
    case 'sunrise':
      gather(ctx, festival);
      return;
    case 'path':
      walk(ctx, festival);
      return;
  }
}

function holdStage({ state, content }: StepContext, festival: FestivalState): void {
  const scene = festival.scenes.at(-1);
  if (scene !== undefined) {
    scene.toTick = state.tick;
  }
  state.events.push({ type: 'festivalSceneHeld', setId: state.setId });
  const left = stagesLeft(festival);
  if (left.length === 0) {
    return;
  }
  for (const enemy of state.enemies) {
    enemy.escaped = true;
    enemy.hp = 0;
  }
  state.projectiles = state.projectiles.filter((shot) => shot.owner.kind !== 'enemy');
  for (const player of state.players) {
    if (player.downed) {
      player.downed = false;
      player.reviveTicks = 0;
      state.events.push({ type: 'playerRevived', playerId: player.id });
    }
    if ((player.suppressedTicks ?? 0) > 0) {
      player.suppressedTicks = 0;
      applyDerivedStats(player, lookup(content.classes, player.classId, 'class'));
    }
    if (player.hp < player.maxHp) {
      state.events.push({
        type: 'playerHealed',
        playerId: player.id,
        amount: player.maxHp - player.hp,
      });
      player.hp = player.maxHp;
    }
  }
  const places = FESTIVAL.exits[state.setId] ?? [];
  festival.exits = left.map((setId, index): FestivalExit => {
    const place = places[index];
    if (place === undefined) {
      throw new Error(`the set "${state.setId}" has no exit toward "${setId}"`);
    }
    return { setId, side: place.side, ...exitSpot(state.arena, place) };
  });
  festival.phase = 'sunrise';
}

function gather(ctx: StepContext, festival: FestivalState): void {
  const { state, tempo } = ctx;
  festival.standing = state.players.map((player) => {
    const index = festival.exits.findIndex((exit) => {
      const dx = player.x - exit.x;
      const dy = player.y - exit.y;
      return dx * dx + dy * dy <= exit.radius * exit.radius;
    });
    return index === -1 ? null : index;
  });
  // A player whose peer left online has no say: the team goes on without them.
  const present = festival.standing.filter((_, slot) => state.players[slot]?.gone !== true);
  const [first] = present;
  const together =
    first !== null && first !== undefined && present.every((index) => index === first);
  festival.departTicks = together ? festival.departTicks + 1 : 0;
  const exit = together ? festival.exits[first] : undefined;
  if (exit !== undefined && festival.departTicks >= FESTIVAL.departBars * tempo.ticksPerBar) {
    leave(ctx, festival, exit);
  }
}

function leave(ctx: StepContext, festival: FestivalState, exit: FestivalExit): void {
  const { state, content } = ctx;
  state.events.push({ type: 'festivalLeft', setId: exit.setId, side: exit.side });
  const path = pathWithObstacles(
    lookup(content.sets, pathId(exit.setId, axisOf(exit.side)), 'set'),
    festival.scenes.at(-1)?.setId,
    exit.setId,
    exit.side,
    state.seed,
    festival.scenes.length,
  );
  const forward = FORWARD[exit.side];
  const start = startOf(path.arena, forward);
  const spots = state.players.map((player) =>
    across(start, forward, offsetIn(player, exit, forward), path.arena, player.radius),
  );
  festival.taken = exit;
  festival.exits = [];
  festival.standing = state.players.map(() => null);
  festival.departTicks = 0;
  festival.phase = 'path';
  enterSet(ctx, path, spots);
}

// On a bar of the stage ahead's tempo, so that its music goes on in time.
function walk(ctx: StepContext, festival: FestivalState): void {
  const { state } = ctx;
  const exit = takenExit(festival);
  if (
    isBarTick(state.tick, ctx.tempo) &&
    state.players.every(
      (player) =>
        player.gone === true || toEnd(player, state.arena, exit.side) <= FESTIVAL.path.endDepth,
    )
  ) {
    arrive(ctx, festival, exit);
  }
}

function takenExit(festival: FestivalState): FestivalExit {
  if (festival.taken === undefined) {
    throw new Error('a festival walks a path without an exit taken');
  }
  return festival.taken;
}

export function toEnd(
  point: Vec2,
  arena: { width: number; height: number },
  side: EdgeSide,
): number {
  switch (side) {
    case 'right':
      return arena.width - point.x;
    case 'left':
      return point.x;
    case 'bottom':
      return arena.height - point.y;
    case 'top':
      return point.y;
  }
}

export function pathWalked(state: SimState): number | null {
  const side = state.festival?.phase === 'path' ? state.festival.taken?.side : undefined;
  const walkers = state.players.filter((player) => player.gone !== true);
  if (side === undefined || walkers.length === 0) {
    return null;
  }
  const length = side === 'left' || side === 'right' ? state.arena.width : state.arena.height;
  const span = length - FESTIVAL.exit.depth - FESTIVAL.path.endDepth;
  const walked = walkers.map((player) =>
    clamp01((length - FESTIVAL.exit.depth - toEnd(player, state.arena, side)) / span),
  );
  return walked.reduce((sum, share) => sum + share, 0) / walked.length;
}

function arrive(ctx: StepContext, festival: FestivalState, exit: FestivalExit): void {
  const { state, content } = ctx;
  const stage = lookup(content.sets, exit.setId, 'set');
  const forward = FORWARD[exit.side];
  const centre = { x: state.arena.width / 2, y: state.arena.height / 2 };
  const entry = entrySpot(stage.arena, stage.id, OPPOSITE[exit.side]);
  const spots = state.players.map((player) =>
    across(entry, forward, offsetIn(player, centre, forward), stage.arena, player.radius),
  );
  state.events.push({ type: 'festivalArrived', setId: stage.id });
  festival.scenes.push({ setId: stage.id, fromTick: state.tick });
  const scale = heldScale(festival.scenes.length - 1);
  if (scale !== undefined) {
    state.enemyScale = scale;
  }
  if (stagesLeft(festival).length === 0) {
    state.lootDraw = FESTIVAL.lastStageLoot;
  }
  delete festival.taken;
  festival.phase = 'scene';
  enterSet(ctx, stage, spots);
}

const FORWARD: Readonly<Record<EdgeSide, Vec2>> = {
  right: { x: 1, y: 0 },
  left: { x: -1, y: 0 },
  bottom: { x: 0, y: 1 },
  top: { x: 0, y: -1 },
};

function startOf(arena: { width: number; height: number }, forward: Vec2): Vec2 {
  const { depth } = FESTIVAL.exit;
  return {
    x: forward.x === 0 ? arena.width / 2 : forward.x > 0 ? depth : arena.width - depth,
    y: forward.y === 0 ? arena.height / 2 : forward.y > 0 ? depth : arena.height - depth,
  };
}

function offsetIn(player: PlayerState, centre: Vec2, forward: Vec2): number {
  const offset = forward.x === 0 ? player.x - centre.x : player.y - centre.y;
  return clamp(offset, -FESTIVAL.exit.radius, FESTIVAL.exit.radius);
}

function across(
  from: Vec2,
  forward: Vec2,
  offset: number,
  arena: { width: number; height: number },
  radius: number,
): Vec2 {
  const x = forward.x === 0 ? from.x + offset : from.x;
  const y = forward.x === 0 ? from.y : from.y + offset;
  return {
    x: clamp(x, radius, arena.width - radius),
    y: clamp(y, radius, arena.height - radius),
  };
}
